import React, { useState } from 'react';
import { sendContactEmail } from '../../lib/api/email';

type MoveInTimeframe =
  | '2_weeks'
  | '1_month'
  | '1_3_months'
  | '3_plus_months';

type PropertyType = 'apartment' | 'house' | 'condo' | '';

interface ConciergeLeadFormState {
  location: string;
  budgetMin: string;
  budgetMax: string;
  propertyType: PropertyType;
  timeframe: MoveInTimeframe | '';
  name: string;
  email: string;
  phone: string;
}

const initialState: ConciergeLeadFormState = {
  location: '',
  budgetMin: '',
  budgetMax: '',
  propertyType: '',
  timeframe: '',
  name: '',
  email: '',
  phone: '',
};

const ConciergeLeadForm: React.FC = () => {
  const [form, setForm] = useState<ConciergeLeadFormState>(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(false);
    setSubmitError(null);
    setSubmitting(true);

    try {
      const budgetLine = [form.budgetMin, form.budgetMax].filter(Boolean).join(' – ');
      const message = [
        `Preferred area: ${form.location}`,
        form.propertyType ? `Property type: ${form.propertyType}` : null,
        form.timeframe ? `Move-in: ${form.timeframe.replace(/_/g, ' ')}` : null,
        budgetLine ? `Budget (₱/mo): ${budgetLine}` : null,
        form.phone ? `Phone: ${form.phone}` : null,
      ]
        .filter(Boolean)
        .join('\n');

      const result = await sendContactEmail({
        name: form.name,
        email: form.email,
        subject: 'Homepage concierge request',
        message,
      });

      if (result.success) {
        setSubmitted(true);
        setForm(initialState);
      } else {
        setSubmitError(result.message || 'Could not send your request. Please try again.');
      }
    } catch {
      setSubmitError('Could not send your request. Please try the contact page or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="hero-search"
      tabIndex={-1}
      aria-label="Find me a home concierge form"
      className="rounded-2xl border border-white/40 bg-white p-6 shadow-2xl sm:p-7 md:p-8 max-w-md w-full"
    >
      <h2 className="text-balance text-xl font-semibold text-neutral-900 sm:text-2xl mb-2 tracking-tight">
        Find me a home
      </h2>
      <p className="mb-6 text-sm leading-relaxed text-neutral-600 sm:text-base">
        Share a few details and a rental specialist will send you curated
        options.
      </p>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="location"
            className="block text-sm font-medium text-neutral-800 mb-1"
          >
            Preferred city or area
          </label>
          <input
            id="location"
            name="location"
            type="text"
            required
            autoComplete="off"
            value={form.location}
            onChange={handleChange}
            placeholder="e.g. BGC, Makati, Cebu City"
            className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="budgetMin"
              className="block text-sm font-medium text-neutral-800 mb-1"
            >
              Budget min (₱/month)
            </label>
            <input
              id="budgetMin"
              name="budgetMin"
              type="number"
              min={0}
              value={form.budgetMin}
              onChange={handleChange}
              placeholder="20000"
              className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <div>
            <label
              htmlFor="budgetMax"
              className="block text-sm font-medium text-neutral-800 mb-1"
            >
              Budget max (₱/month)
            </label>
            <input
              id="budgetMax"
              name="budgetMax"
              type="number"
              min={0}
              value={form.budgetMax}
              onChange={handleChange}
              placeholder="40000"
              className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="propertyType"
              className="block text-sm font-medium text-neutral-800 mb-1"
            >
              Property type
            </label>
            <select
              id="propertyType"
              name="propertyType"
              value={form.propertyType}
              onChange={handleChange}
              className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-900 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Any</option>
              <option value="apartment">Apartment</option>
              <option value="house">House</option>
              <option value="condo">Condo</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="timeframe"
              className="block text-sm font-medium text-neutral-800 mb-1"
            >
              Move-in timeframe
            </label>
            <select
              id="timeframe"
              name="timeframe"
              value={form.timeframe}
              onChange={handleChange}
              className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-900 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Select</option>
              <option value="2_weeks">Within 2 weeks</option>
              <option value="1_month">Within 1 month</option>
              <option value="1_3_months">1–3 months</option>
              <option value="3_plus_months">3+ months</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-neutral-800 mb-1"
            >
              Full name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              value={form.name}
              onChange={handleChange}
              placeholder="Juan Dela Cruz"
              className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-neutral-800 mb-1"
            >
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={form.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-neutral-800 mb-1"
          >
            Phone (optional)
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            placeholder="+63..."
            className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="mt-2 inline-flex w-full items-center justify-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary-600/30 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting ? 'Submitting...' : 'Get custom matches'}
        </button>

        <p className="mt-2 text-xs text-neutral-500">
          No spam. A specialist will contact you within 24 hours.
        </p>

        {submitError && (
          <p className="mt-3 text-xs font-medium text-error-700 bg-error-50 border border-error-100 rounded-md px-3 py-2">
            {submitError}
          </p>
        )}

        {submitted && (
          <p className="mt-3 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2">
            Thank you! We’ve received your request and will get back to you
            soon.
          </p>
        )}
      </form>
    </section>
  );
};

export default ConciergeLeadForm;
